import React, { useState, useEffect } from 'react';
import { Box, Card, CardContent, Typography, TextField, Grid, MenuItem } from '@mui/material';
import { useThemeContext } from '../../../hook/useThemeManager';
import { theme } from '../../../constants/theme';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import { ICompany, ILocation } from '../../../redux/company/types';
import { COMPANY_TYPES } from '../../../data/forms/company.data';
import { useDispatch } from 'react-redux';
import { updateCompany } from '../../../redux/company/actions';
import PictureUploader from '../../PictureUploader/PictureUploader';
import { CustomDatePicker } from '../../DatePicker/CustomDatePicker';
import LocationAutocomplete from '../../LocationAutocomplete/LocationAutocomplete';
import dayjs from 'dayjs';
import { currencySymbols } from '../../../constants/currency';
import { COMPANY_BRAND_ORANGE } from '../../../constants/companyBrand';

interface ICompanyBasicInfoEdit {
    company?: ICompany;
    isEditing: boolean;
    onSave: (saveFunction: () => void) => void;
    onChanges: (hasChanges: boolean) => void;
}

const menuPaperSx = {
    backgroundColor: '#000000',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: '12px',
    '& .MuiMenuItem-root': {
        color: '#ffffff',
        fontFamily: FONT_ENUM.MAIN_MEDIUM,
        '&:hover': {
            backgroundColor: 'rgba(255,255,255,0.08)',
        },
        '&.Mui-selected': {
            backgroundColor: `${COMPANY_BRAND_ORANGE}2a`,
            color: COMPANY_BRAND_ORANGE,
            '&:hover': {
                backgroundColor: `${COMPANY_BRAND_ORANGE}38`,
            },
        },
    },
};

const CompanyBasicInfoEdit: React.FC<ICompanyBasicInfoEdit> = ({ company, isEditing, onSave, onChanges }) => {
    const { themeStyle } = useThemeContext();
    const dispatch = useDispatch();
    const [formData, setFormData] = useState({
        company_name: company?.company_name || '',
        company_type: company?.company_type || 'media company',
        date_of_formation: company?.date_of_formation ? dayjs(company.date_of_formation) : null,
        min_employees: company?.min_employees || 1,
        max_employees: company?.max_employees || 10,
        min_revennue: company?.min_revennue || 1,
        max_revenue: company?.max_revenue || 1000,
        currency: company?.currency || 'USD',
        profile_picture: company?.profile_picture || '',
        rawProfilePicture: {},
        location: company?.location || { address: '', description: '', coordinates: [], type: 'Point' },
    });
    const [initialData, setInitialData] = useState(formData);

    useEffect(() => {
        if (company) {
            const newData = {
                company_name: company.company_name || '',
                company_type: company.company_type || 'media company',
                date_of_formation: company.date_of_formation ? dayjs(company.date_of_formation) : null,
                min_employees: company.min_employees || 1,
                max_employees: company.max_employees || 10,
                min_revennue: company.min_revennue || 1,
                max_revenue: company.max_revenue || 1000,
                currency: company.currency || 'USD',
                profile_picture: company.profile_picture || '',
                rawProfilePicture: {},
                location: company.location || { address: '', description: '', coordinates: [], type: 'Point' },
            };
            setFormData(newData);
            setInitialData(newData);
        }
    }, [company]);

    useEffect(() => {
        const hasChanges = JSON.stringify(formData) !== JSON.stringify(initialData);
        onChanges(hasChanges);
    }, [formData, initialData, onChanges]);

    const handleFieldChange = (field: string, value: any) => {
        setFormData(prev => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleSave = () => {
        if (company?._id) {
            const payload: any = {
                id: company._id,
                body: {
                    company_name: formData.company_name,
                    company_type: formData.company_type,
                    ...(formData.date_of_formation && { date_of_formation: formData.date_of_formation.toISOString() }),
                    min_employees: formData.min_employees,
                    max_employees: formData.max_employees,
                    min_revennue: formData.min_revennue,
                    max_revenue: formData.max_revenue,
                    currency: formData.currency,
                    location: formData.location,
                    profile_picture: formData.rawProfilePicture,
                },
            };
            dispatch(updateCompany(payload));
        }
    };

    useEffect(() => {
        onSave(handleSave);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onSave, formData, company?._id]);

    const handleImageChange = (
        image: string | any[] | null,
        rawFile?: File[] | FileList | null,
        alternateKey?: string,
    ) => {
        handleFieldChange('profile_picture', image);
        handleFieldChange('rawProfilePicture', rawFile);
    };

    const handleLocationSelect = (address: string, coordinates?: number[], formattedAddress?: ILocation) => {
        if (formattedAddress) {
            handleFieldChange('location', formattedAddress);
        } else {
            handleFieldChange('location', {
                ...formData.location,
                address,
                coordinates: coordinates || [],
            });
        }
    };

    const sharedOutlinedRoot = {
        borderRadius: 2,
        bgcolor: '#000000',
        '& fieldset': {
            borderColor: 'rgba(255,255,255,0.22)',
        },
        '&:hover fieldset': {
            borderColor: isEditing ? 'rgba(255,255,255,0.4)' : 'rgba(255,255,255,0.22)',
        },
        '&.Mui-focused fieldset': {
            borderColor: COMPANY_BRAND_ORANGE,
            borderWidth: '1px',
        },
        '&.Mui-disabled fieldset': {
            borderColor: 'rgba(255,255,255,0.12)',
        },
    };

    /** Shared by plain inputs and `TextField select` so dropdowns match e.g. Maximum Employees. */
    const outlinedFieldSx = {
        '& .MuiOutlinedInput-root': sharedOutlinedRoot,
        '& .MuiInputLabel-root': {
            color: 'rgba(255,255,255,0.65)',
        },
        '& .MuiInputLabel-root.Mui-focused': {
            color: COMPANY_BRAND_ORANGE,
        },
        '& .MuiInputBase-input': {
            color: '#ffffff',
        },
        '& .MuiInputBase-input.Mui-disabled': {
            WebkitTextFillColor: 'rgba(255,255,255,0.45)',
        },
        '& .MuiSelect-select': {
            color: '#ffffff',
            backgroundColor: '#000000',
        },
        '& .MuiSelect-select.Mui-disabled': {
            WebkitTextFillColor: 'rgba(255,255,255,0.45)',
        },
        '& .MuiSelect-icon': {
            color: 'rgba(255,255,255,0.55)',
        },
    };

    const selectMenuProps = {
        MenuProps: {
            PaperProps: {
                sx: menuPaperSx,
            },
        },
    } as const;

    const sectionTitleSx = {
        color: '#ffffff',
        fontFamily: FONT_ENUM.BOLD_HEADER,
        fontWeight: 700,
        fontSize: { xs: '1.125rem', sm: '1.25rem' },
        mb: 2,
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box>
                <Typography
                    component="h1"
                    sx={{
                        fontFamily: FONT_ENUM.BOLD_HEADER,
                        fontWeight: 800,
                        fontSize: { xs: '1.75rem', md: '2.25rem' },
                        color: '#ffffff',
                        letterSpacing: '-0.02em',
                        mb: 1.5,
                    }}>
                    Basic{' '}
                    <Box component="span" sx={{ color: COMPANY_BRAND_ORANGE }}>
                        Information
                    </Box>
                </Typography>
                <Typography
                    sx={{
                        color: 'rgba(255,255,255,0.55)',
                        maxWidth: 560,
                        lineHeight: 1.65,
                        fontSize: '0.95rem',
                        fontFamily: FONT_ENUM.MEDIUM_TEXT,
                    }}>
                    Legal name, scale, location, and branding essentials for your company profile.
                </Typography>
            </Box>

            <Card
                sx={{
                    bgcolor: '#1a1a1a',
                    border: '1px solid rgba(255,255,255,0.06)',
                    borderRadius: 3,
                    boxShadow: 'none',
                }}>
                <CardContent sx={{ p: { xs: 2.5, sm: 3, md: 4 } }}>
                    <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Company Name"
                            value={formData.company_name}
                            onChange={e => handleFieldChange('company_name', e.target.value)}
                            disabled={!isEditing}
                            sx={outlinedFieldSx}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            select
                            fullWidth
                            label="Company Type"
                            value={formData.company_type}
                            onChange={e => handleFieldChange('company_type', e.target.value)}
                            disabled={!isEditing}
                            sx={outlinedFieldSx}
                            SelectProps={selectMenuProps}>
                            {COMPANY_TYPES.map(type => (
                                <MenuItem key={type} value={type}>
                                    {type}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <CustomDatePicker
                            label="Date of Formation"
                            value={formData.date_of_formation ? formData.date_of_formation.toISOString() : undefined}
                            onChange={date => handleFieldChange('date_of_formation', date ? dayjs(date) : null)}
                            disabled={!isEditing}
                            withTimezone={false}
                            uniformInputBlack
                            sx={{
                                border: '1px solid rgba(255,255,255,0.22)',
                                borderRadius: 2,
                                backgroundColor: '#000000',
                                '&:focus-within': {
                                    border: `1px solid ${COMPANY_BRAND_ORANGE}`,
                                },
                            }}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Typography variant="h6" sx={sectionTitleSx}>
                            Company Headquaters
                        </Typography>
                        <LocationAutocomplete
                            value={{
                                address: formData.location.address || '',
                                description: formData.location.description || '',
                                coordinates: formData.location.coordinates || [],
                            }}
                            onSelect={handleLocationSelect}
                            onSelectLocationOption={() => {}}
                            placeholder="Search for company location..."
                            withCordinates
                            searchOptions={{ types: ['(cities)'] }}
                            uniformPageBlack
                            fieldStyle={{
                                bgcolor: '#000000',
                                borderRadius: '8px',
                                input: { color: '#ffffff' },
                                marginTop: '0px',
                                label: { color: 'rgba(255,255,255,0.65)' },
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'rgba(255,255,255,0.22)',
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: isEditing ? 'rgba(255,255,255,0.4)' : 'rgba(255,255,255,0.22)',
                                },
                                '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: COMPANY_BRAND_ORANGE,
                                },
                            }}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Minimum Employees"
                            type="number"
                            value={formData.min_employees}
                            onChange={e => handleFieldChange('min_employees', parseInt(e.target.value) || 1)}
                            disabled={!isEditing}
                            sx={outlinedFieldSx}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Maximum Employees"
                            type="number"
                            value={formData.max_employees}
                            onChange={e => handleFieldChange('max_employees', parseInt(e.target.value) || 10)}
                            disabled={!isEditing}
                            sx={outlinedFieldSx}
                        />
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            label="Minimum Revenue"
                            type="number"
                            value={formData.min_revennue}
                            onChange={e => handleFieldChange('min_revennue', parseInt(e.target.value) || 1)}
                            disabled={!isEditing}
                            sx={outlinedFieldSx}
                        />
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            label="Maximum Revenue"
                            type="number"
                            value={formData.max_revenue}
                            onChange={e => handleFieldChange('max_revenue', parseInt(e.target.value))}
                            disabled={!isEditing}
                            sx={outlinedFieldSx}
                        />
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <TextField
                            select
                            fullWidth
                            label="Currency"
                            value={formData.currency}
                            onChange={e => handleFieldChange('currency', e.target.value)}
                            disabled={!isEditing}
                            sx={outlinedFieldSx}
                            SelectProps={selectMenuProps}>
                            {Object.keys(currencySymbols).map(cur => (
                                <MenuItem key={cur} value={cur}>
                                    {cur}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>

                    {/* Profile Picture — unchanged styling / behaviour per product request */}
                    <Grid item xs={12}>
                        <Typography
                            variant="h6"
                            sx={{
                                color: theme[themeStyle].tag_white,
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                mb: 2,
                            }}>
                            Profile Picture
                        </Typography>
                        <PictureUploader
                            initialImageSrc={formData.profile_picture}
                            onChange={handleImageChange}
                            onRemove={() => handleImageChange(null)}
                            aspect={1}
                            sx={{ width: '100%', maxWidth: '300px' }}
                            isEditMode={isEditing}
                        />
                    </Grid>
                </Grid>
                </CardContent>
            </Card>
        </Box>
    );
};

export { CompanyBasicInfoEdit };
